import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "ll_token";

export const api = axios.create({ baseURL: API_BASE, timeout: 60000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/signup")) {
      localStorage.removeItem(TOKEN_KEY);
      if (!window.location.pathname.startsWith("/login")) window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const errorMessage = (error, fallback = "Something went wrong") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || fallback;
  return error?.message || fallback;
};

// ------------------------------------------------------------------ endpoints
export const AuthApi = {
  signup: (payload) => api.post("/auth/signup", payload).then((r) => r.data),
  login: (payload) => api.post("/auth/login", payload).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  demoSeed: () => api.post("/auth/demo-seed").then((r) => r.data),
  invites: () => api.get("/auth/invites").then((r) => r.data),
  createInvite: (payload) => api.post("/auth/invites", payload).then((r) => r.data),
  revokeInvite: (id) => api.delete(`/auth/invites/${id}`).then((r) => r.data),
  resendInvite: (id) => api.post(`/auth/invites/${id}/resend`).then((r) => r.data),
  getInvite: (token) => api.get(`/auth/invite/${token}`).then((r) => r.data),
  acceptInvite: (token, payload) =>
    api.post(`/auth/invite/${token}/accept`, payload).then((r) => r.data),
};

export const ReceiptsApi = {
  upload: (files, onProgress, splitPdf = false) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return api
      .post("/receipts/upload", form, {
        params: splitPdf ? { split_pdf: true } : undefined,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
        onUploadProgress: (e) =>
          onProgress && onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      })
      .then((r) => r.data);
  },
  sample: (name) => api.post(`/receipts/sample?name=${encodeURIComponent(name)}`).then((r) => r.data),
  samples: () => api.get("/receipts/samples").then((r) => r.data),
  quota: () => api.get("/receipts/quota").then((r) => r.data),
  list: (params) => api.get("/receipts", { params }).then((r) => r.data),
  feed: () => api.get("/receipts/feed").then((r) => r.data),
  get: (id) => api.get(`/receipts/${id}`).then((r) => r.data),
  image: (id) => api.get(`/receipts/${id}/image`, { responseType: "blob" }).then((r) => r.data),
  update: (id, payload) => api.patch(`/receipts/${id}`, payload).then((r) => r.data),
  approve: (id) => api.post(`/receipts/${id}/approve`).then((r) => r.data),
  reject: (id, reason) => api.post(`/receipts/${id}/reject`, { reason }).then((r) => r.data),
  reprocess: (id) => api.post(`/receipts/${id}/reprocess`).then((r) => r.data),
  bulkApprove: (ids) => api.post("/receipts/bulk/approve", { ids }).then((r) => r.data),
  markPaid: (id) => api.post(`/receipts/${id}/payout`).then((r) => r.data),
  unmarkPaid: (id) => api.delete(`/receipts/${id}/payout`).then((r) => r.data),
  bulkMarkPaid: (ids) => api.post("/receipts/bulk/payout", { ids }).then((r) => r.data),
  remove: (id) => api.delete(`/receipts/${id}`).then((r) => r.data),
};

export const DashboardApi = {
  summary: (params) => api.get("/dashboard/summary", { params }).then((r) => r.data),
  mySummary: () => api.get("/dashboard/my-summary").then((r) => r.data),
  audit: () => api.get("/dashboard/audit").then((r) => r.data),
};

export const SettingsApi = {
  setAdminSubType: (admin_sub_type) =>
    api.put("/settings/admin-sub-type", { admin_sub_type }).then((r) => r.data),
  get: () => api.get("/settings").then((r) => r.data),
  updateCompany: (payload) => api.put("/settings/company", payload).then((r) => r.data),
  updateVatRates: (vat_rates) => api.put("/settings/vat-rates", { vat_rates }).then((r) => r.data),
  updateCategories: (categories) =>
    api.put("/settings/categories", { categories }).then((r) => r.data),
  updateRefundRules: (payload) => api.put("/settings/refund-rules", payload).then((r) => r.data),
};

export const ClientsApi = {
  list: () => api.get("/clients").then((r) => r.data),
  create: (payload) => api.post("/clients", payload).then((r) => r.data),
  switchTo: (id) => api.post(`/clients/${id}/switch`).then((r) => r.data),
  unlink: (id) => api.delete(`/clients/${id}`).then((r) => r.data),
};

export const TeamApi = {
  members: () => api.get("/team/members").then((r) => r.data),
  update: (id, payload) => api.patch(`/team/members/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/team/members/${id}`).then((r) => r.data),
  setOverride: (id, payload) =>
    api.put(`/team/members/${id}/refund-override`, payload).then((r) => r.data),
  clearOverride: (id) => api.delete(`/team/members/${id}/refund-override`).then((r) => r.data),
};

export const ReportsApi = {
  preview: (params) => api.get("/reports/preview", { params }).then((r) => r.data),
  exportJob: (id) => api.get(`/reports/export/jobs/${id}`).then((r) => r.data),
  exportJobs: () => api.get("/reports/export/jobs").then((r) => r.data),
  download: (params, format) =>
    api
      .get("/reports/export", {
        params: { ...params, format },
        responseType: "blob",
        // a queued export answers 202 with JSON instead of a file
        validateStatus: (status) => status === 200 || status === 202,
      })
      .then(async (r) => {
        if (r.status === 202) {
          const text = typeof r.data?.text === "function" ? await r.data.text() : "{}";
          let payload = {};
          try {
            payload = JSON.parse(text);
          } catch (e) {
            payload = {};
          }
          return { queued: true, ...payload };
        }
        return {
          blob: r.data,
          disposition: r.headers["content-disposition"],
          documentNo: r.headers["x-document-number"],
          rowCount: Number(r.headers["x-row-count"] ?? NaN),
        };
      }),
  payout: (params) => api.get("/reports/payout", { params }).then((r) => r.data),
  payoutExport: (params) =>
    api
      .get("/reports/payout/export", { params, responseType: "blob" })
      .then((r) => ({ blob: r.data, disposition: r.headers["content-disposition"],
                      rowCount: Number(r.headers["x-row-count"] ?? NaN) })),
  documents: (limit = 25) =>
    api.get("/reports/documents", { params: { limit } }).then((r) => r.data),
  recipients: () => api.get("/reports/recipients").then((r) => r.data),
  email: (payload) => api.post("/reports/email", payload).then((r) => r.data),
  monthlyRun: () => api.post("/reports/monthly/run").then((r) => r.data),
};

export const DuplicatesApi = {
  list: () => api.get("/duplicates").then((r) => r.data),
  count: () => api.get("/duplicates/count").then((r) => r.data),
  keep: (id) => api.post(`/duplicates/${id}/keep`).then((r) => r.data),
  confirm: (id) => api.post(`/duplicates/${id}/confirm`).then((r) => r.data),
  remove: (id) => api.delete(`/duplicates/${id}`).then((r) => r.data),
};

export const NotificationsApi = {
  status: () => api.get("/notifications/status").then((r) => r.data),
  updateSettings: (payload) => api.put("/notifications/settings", payload).then((r) => r.data),
  log: () => api.get("/notifications/log").then((r) => r.data),
  test: (recipient) => api.post("/notifications/test", { recipient }).then((r) => r.data),
  feed: (params) => api.get("/notifications/feed", { params }).then((r) => r.data),
  unreadCount: () => api.get("/notifications/unread-count").then((r) => r.data),
  markRead: (id) => api.post(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => api.post("/notifications/read-all").then((r) => r.data),
};

export const AuditApi = {
  list: (params) => api.get("/audit", { params }).then((r) => r.data),
  filters: () => api.get("/audit/filters").then((r) => r.data),
};

export const BillingApi = {
  config: () => api.get("/billing/config").then((r) => r.data),
  subscription: () => api.get("/billing/subscription").then((r) => r.data),
  quote: (params) => api.get("/billing/quote", { params }).then((r) => r.data),
  checkout: (payload) => api.post("/billing/checkout", payload).then((r) => r.data),
  status: (sessionId) => api.get(`/billing/status/${sessionId}`).then((r) => r.data),
};
